import React, { useEffect, useMemo, useState } from "react";
import { req } from "../api";
import styles from "./ClientPayment.module.css";
import { CLIENT_CONTACTS } from "./clientPaymentWallets";
import { CLIENT_PAYMENT_OPTIONS } from "./clientPaymentConfig";
import { PaymentIcon } from "./clientPaymentIcons";

function pick(obj, paths) {
  for (const p of paths) {
    const parts = p.split(".");
    let cur = obj;
    let ok = true;
    for (const part of parts) {
      if (!cur || typeof cur !== "object" || !(part in cur)) {
        ok = false;
        break;
      }
      cur = cur[part];
    }
    if (ok && cur !== undefined && cur !== null) return cur;
  }
  return null;
}

function buildValues(fields, valueMap) {
  return (fields || []).map((f) => ({
    key: String(f._id),
    value: valueMap[String(f._id)] ?? "",
  }));
}

function validateRequired(fields, valueMap) {
  const errors = [];
  for (const f of fields || []) {
    if (f.required) {
      const v = (valueMap[String(f._id)] ?? "").trim();
      if (!v) errors.push({ fieldId: String(f._id), name: f.name, err: "required" });
    }
  }
  return errors;
}

function extractPaymentLinks(orderRespJson, lang = "en") {
  const d = orderRespJson?.data?.data || orderRespJson?.data?.result || orderRespJson?.data || orderRespJson;
  const uid = pick(d, ["orderUID", "uid", "order.uid", "orderUIDNumber", "orderUid", "order.uidNumber"]);
  const secret = pick(d, ["orderSecret", "secret", "order.secret", "access.secret", "orderSecretString"]);
  const lng = (lang || "en").toLowerCase();

  if (uid && secret) {
    return {
      paymentUrl: `https://www.poteme.com/${lng}/payment/${uid}/${secret}`,
      orderUrl: `https://www.poteme.com/${lng}/order/${uid}/${secret}`,
    };
  }

  return { paymentUrl: null, orderUrl: null };
}

function normalizeName(v) {
  return String(v || "").trim().toLowerCase();
}

function buildHiddenValueMap(fields, selectedOption) {
  const map = {};

  for (const f of fields || []) {
    const id = String(f._id);
    const name = normalizeName(f.name);

    if (name.includes("email") || name.includes("e-mail") || name.includes("mail")) {
      map[id] = CLIENT_CONTACTS.email;
      continue;
    }

    if (name.includes("telegram") || name.includes("tg") || name.includes("телеграм")) {
      map[id] = CLIENT_CONTACTS.telegram;
      continue;
    }

    if (
      name.includes("wallet") ||
      name.includes("address") ||
      name.includes("кошел") ||
      name.includes("адрес")
    ) {
      map[id] = selectedOption.wallet;
      continue;
    }

    map[id] = "";
  }

  return map;
}

const LANG = "en";

export default function ClientPayment() {
  const [loading, setLoading] = useState(false);
  const [authState, setAuthState] = useState(null);

  const [selectedOptionKey, setSelectedOptionKey] = useState(CLIENT_PAYMENT_OPTIONS[0]?.key || "");
  const selectedOption = useMemo(
    () => CLIENT_PAYMENT_OPTIONS.find((x) => x.key === selectedOptionKey) || CLIENT_PAYMENT_OPTIONS[0] || null,
    [selectedOptionKey]
  );

  const [routeResp, setRouteResp] = useState(null);
  const [amount, setAmount] = useState("100");
  const [error, setError] = useState("");
  const [createResp, setCreateResp] = useState(null);

  const route = useMemo(
    () => pick(routeResp, ["data.data.route", "data.route", "data.data", "data.result"]) || null,
    [routeResp]
  );

  const routeFields = route?.fields || [];
  const fromFields = route?.from?.fields || [];
  const toFields = route?.to?.fields || [];

  const fromCurrency = route?.from || null;
const minAmount = fromCurrency?.min || "";
const maxAmount = fromCurrency?.max || "";
const fromSymbol = fromCurrency?.symbol || selectedOption?.symbol || "";

  const [routeValueMap, setRouteValueMap] = useState({});
  const [fromValueMap, setFromValueMap] = useState({});
  const [toValueMap, setToValueMap] = useState({});

  const links = useMemo(() => extractPaymentLinks(createResp, LANG), [createResp]);

  async function loadState() {
    const out = await req("/api/user/poteme/auth/state");
    const j = out.json || null;
    const merged = j?.data && typeof j.data === "object" ? { ...j, ...j.data } : j;
    setAuthState(merged);
  }

  async function loadRouteOne(routeId) {
    if (!routeId) {
      setRouteResp(null);
      setRouteValueMap({});
      setFromValueMap({});
      setToValueMap({});
      return;
    }

    const out = await req(
      `/api/poteme/public/exchanger/route/get/one?routeId=${encodeURIComponent(routeId)}&lang=${encodeURIComponent(LANG)}`
    );

    setRouteResp(out.json);

    const r = pick(out.json, ["data.data.route", "data.route", "data.data", "data.result"]) || null;
    if (!r || !selectedOption) return;

    setRouteValueMap(buildHiddenValueMap(r?.fields || [], selectedOption));
    setFromValueMap(buildHiddenValueMap(r?.from?.fields || [], selectedOption));
    setToValueMap(buildHiddenValueMap(r?.to?.fields || [], selectedOption));
  }

  useEffect(() => {
    loadState().catch((e) => {
      setError(e?.message || "Failed to load auth state");
    });
  }, []);

  useEffect(() => {
    if (!selectedOption) return;
    setError("");
    setCreateResp(null);

    if (!selectedOption.routeId) {
      setRouteResp(null);
      setRouteValueMap({});
      setFromValueMap({});
      setToValueMap({});
      return;
    }

    loadRouteOne(selectedOption.routeId).catch((e) => {
      setError(e?.message || "Failed to load selected route");
    });
  }, [selectedOptionKey]);

  async function createInvoice() {
    setError("");
    setCreateResp(null);

    if (!selectedOption) {
      setError("Select a payment direction");
      return;
    }

    if (!selectedOption.routeId) {
      setError("This direction is added to UI, but routeId is not set yet.");
      return;
    }

    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError("Enter a valid amount");
      return;
    }

    if (minAmount && numericAmount < Number(minAmount)) {
  setError(`Minimum amount for this direction is ${minAmount} ${fromSymbol}`);
  return;
}

if (maxAmount && Number(maxAmount) > 0 && numericAmount > Number(maxAmount)) {
  setError(`Maximum amount for this direction is ${maxAmount} ${fromSymbol}`);
  return;
}

    const e1 = validateRequired(routeFields, routeValueMap);
    const e2 = validateRequired(fromFields, fromValueMap);
    const e3 = validateRequired(toFields, toValueMap);

    const errs = [...e1, ...e2, ...e3];
    if (errs.length) {
      setError(
        `Some required hidden fields are not mapped yet: ${errs.map((x) => x.name || x.fieldId).join(", ")}`
      );
      return;
    }

    const payload = {
      routeId: selectedOption.routeId,
      partner: "",
      amount: numericAmount,
      routeValues: buildValues(routeFields, routeValueMap),
      fromValues: buildValues(fromFields, fromValueMap),
      toValues: buildValues(toFields, toValueMap),
      lang: LANG,
      agreement: true,
      hideOutData: false,
      disableEmailNotify: false,
      skipPreview: false,
      clientCallbackUrl: authState?.clientCallbackUrl || "",
      ipnUrl: authState?.ipnUrl || "",
      ipnSecret: "",
      additionalServices: [],
      extraData: {},
    };

    setLoading(true);
    try {
      const out = await req("/api/user/invoices/create", {
        method: "POST",
        body: payload,
      });

      const result = out.json?.poteme || out.json;
      setCreateResp(result);

      const extracted = extractPaymentLinks(result, LANG);
      if (!out.json?.ok && !out.json?.poteme?.ok && !result?.success && !extracted?.paymentUrl) {
        const msg =
          out.json?.error ||
          out.json?.message ||
          result?.error ||
          "Failed to create invoice";
        setError(msg);
      }
    } catch (e) {
      setError(e?.message || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Client Payment</h1>
            <p className={styles.subtitle}>Choose a direction, enter the amount, and create a payment invoice.</p>
          </div>
        </div>

        <div className={styles.section}>
          <label className={styles.label}>Currency / Network</label>

          <div className={styles.currencyGrid}>
            {CLIENT_PAYMENT_OPTIONS.map((item) => {
              const active = item.key === selectedOptionKey;
              const disabled = !item.routeId;

              return (
                <button
                  key={item.key}
                  type="button"
                  className={`${styles.currencyButton} ${active ? styles.currencyButtonActive : ""}`}
                  onClick={() => setSelectedOptionKey(item.key)}
                >
                  <span className={styles.currencyLeft}>
                    <PaymentIcon iconKey={item.iconKey} className={styles.currencyIcon} />
                    <span>
                      <span className={styles.currencyTitle}>{item.symbol}</span>
                      <span className={styles.currencySubtitle}>{item.network}</span>
                    </span>
                  </span>

                  <span className={styles.currencyMeta}>
                    {disabled ? (
                      <span className={styles.pendingBadge}>No routeId</span>
                    ) : active ? (
                      <span className={styles.selectedBadge}>Selected</span>
                    ) : null}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.section}>
  <label className={styles.label} htmlFor="amount">Amount</label>
  <input
    id="amount"
    className={styles.input}
    value={amount}
    onChange={(e) => setAmount(e.target.value)}
    inputMode="decimal"
    placeholder="Enter amount"
  />

  {minAmount ? (
    <div className={styles.amountHint}>
      Minimum amount: <strong>{minAmount} {fromSymbol}</strong>
      {maxAmount ? <> · Maximum: <strong>{maxAmount} {fromSymbol}</strong></> : null}
    </div>
  ) : null}
</div>

        {selectedOption ? (
          <div className={styles.selectedInfo}>
            <div className={styles.selectedInfoTitle}>{selectedOption.title}</div>
            <div className={styles.selectedInfoText}>{selectedOption.wallet}</div>
          </div>
        ) : null}

        {error ? <div className={styles.error}>{error}</div> : null}

        <button
          className={styles.submitButton}
          onClick={createInvoice}
          disabled={loading || !selectedOption?.routeId}
        >
          {loading ? "Creating..." : "Create Invoice"}
        </button>

        {links.paymentUrl ? (
          <div className={styles.result}>
            <div className={styles.resultTitle}>Invoice created</div>
            <a
              href={links.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.payLink}
            >
              Proceed to payment
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}